import {
    CSSProperties,
    PropsWithChildren,
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";

import { css, keyframes } from "@compiled/react";
//@ts-ignore
import { useInView } from "react-intersection-observer";

import { isReducedMotion } from "@atlaskit/motion";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { GalleryControls } from "@/libraries/design/components";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import {
    WCL_GRID_MAX_WIDTH,
    WCL_GRID_MAX_WIDTH_XL,
    WCL_GRID_PADDING_LG,
    WCL_GRID_PADDING_SM,
    WCL_GRID_PADDING_XL
} from "@/libraries/design/foundations/grid/src/base";

export interface GalleryContainerProps extends PropsWithChildren {
    // Additional motion within the scrolling container can be disabled
    isMotionAvailable?: boolean;
    motionDelay?: string;
    // Gap between gallery items in pixels. Defaults to space.400 (16px) below md and space.600 (24px) above md.
    gap?: { aboveMd: string; belowMd: string };
    showEntry?: boolean;
}

// All CSS Custom Properties prefixed --gc-scroll are to power scroll driven animations on sub elements
// Additional necessary @property rules appear in root index.css file
// Based on: https://www.bram.us/2023/10/23/css-scroll-detection/
const scrollAnimations = keyframes({
    "100%": {
        "--gc-scroll-position": `1`,
        "--gc-scroll-position-delayed": `1`
    }
});

const galleryStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "end",
    gap: token("space.400"),
    [media.above.md]: {
        gap: token("space.500")
    }
});

const scrollableStyles = css({
    // Aligns to the grid but bleeds beyond the end, inline direction
    "width": "100%",
    "overflowY": "hidden",
    "overflowX": "auto",
    ["@supports (scrollbar-width: none)"]: {
        scrollbarWidth: "none"
    },
    "paddingBlockStart": token("space.300"),
    "scrollBehavior": "smooth",
    "scrollSnapType": "x mandatory",
    "paddingInline": `var(--cl-overflow-bleed, 0px)`,
    "scrollPaddingInline": `var(--cl-overflow-bleed, 0px)`,
    "--cl-overflow-bleed": `${WCL_GRID_PADDING_SM}px`,
    [media.only.sm]: {
        "--cl-overflow-bleed": `${WCL_GRID_PADDING_LG}px`
    },
    [media.only.md]: {
        "--cl-overflow-bleed": `max(${WCL_GRID_PADDING_LG}px, calc((100% - ${
            WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2
        }px) / 2 + ${WCL_GRID_PADDING_LG}px))`
    },
    [media.above.lg]: {
        "--cl-overflow-bleed": `max(${WCL_GRID_PADDING_XL}px, calc((100% - ${
            WCL_GRID_MAX_WIDTH_XL + WCL_GRID_PADDING_XL * 2
        }px) / 2 + ${WCL_GRID_PADDING_XL}px))`
    }
});

const scrollableAnimation = css({
    "@media (prefers-reduced-motion: no-preference)": {
        animationName: scrollAnimations,
        animationTimingFunction: "linear",
        animationFillMode: "both",
        animationTimeline: "scroll(x self)",
        overscrollBehaviorInline: "contain"
    }
});

// Applied when all content fits within the viewport — centers the inner row and removes bleed/scroll behaviour
const scrollableCenteredStyles = css({
    display: "flex",
    justifyContent: "center",
    paddingInline: 0,
    scrollPaddingInline: 0
});

const innerStyles = css({
    "display": "flex",
    "flexDirection": "row",
    "alignItems": "end",
    "width": "max-content",
    "--wcl-gallery-gap": `var(--gc-gap-below-md, ${token("space.400")})`,
    "gap": "var(--wcl-gallery-gap)",
    [media.above.md]: {
        "--wcl-gallery-gap": `var(--gc-gap-above-md, ${token("space.600")})`
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > *": {
        scrollSnapAlign: "start",
        [media.below.sm]: {
            scrollSnapAlign: "center"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& > span": {
        "&:first-of-type": {
            marginInlineEnd: `calc(var(--wcl-gallery-gap) * -1)`
        },
        "&:last-of-type": {
            marginInlineStart: `calc(var(--wcl-gallery-gap) * -1)`
        }
    },

    // motion setup for scrolling
    "transition": "--gc-scroll-position-delayed 166.667ms linear",
    "--gc-scroll-velocity":
        "calc(var(--gc-scroll-position) - var(--gc-scroll-position-delayed))",
    "--gc-scroll-speed":
        "max(var(--gc-scroll-velocity), -1 * var(--gc-scroll-velocity))",
    "--gc-scroll-direction":
        "calc(var(--gc-scroll-velocity) / var(--gc-scroll-speed))"
});

const entryFrames = keyframes({
    from: {
        transform: "translateX(1400px)",
        opacity: 0
    }
});
const entrySetup = css({
    "@media (prefers-reduced-motion: no-preference)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".entry-item": {
            transform: "translateX(0)",
            opacity: 1,
            animationName: entryFrames,
            animationDuration: "750ms",
            animationTimingFunction: wacToken("motion.easing.ease40-out"),
            animationDelay: "calc(var(--gc-item-i, 0) * 66.667ms)",
            animationFillMode: "backwards",
            animationPlayState: "paused"
        }
    }
});
const entryAnimation = css({
    "@media (prefers-reduced-motion: no-preference)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".entry-item": {
            animationPlayState: "running"
        }
    }
});

const controlsStyles = css({
    transition: "opacity 75ms linear"
});
const controlsHiddenStyles = css({
    opacity: 0
});

const SCROLL_BUFFER = 40;

export const GalleryContainer = ({
    isMotionAvailable,
    motionDelay,
    gap,
    showEntry = true,
    children
}: GalleryContainerProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const innerRef = useRef<HTMLDivElement>(null);

    // True when the inner content row fits entirely within the scroll container's visible width
    const [isFitting, setIsFitting] = useState(false);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        const innerEl = innerRef.current;
        if (!scrollEl || !innerEl) {
            return;
        }

        const observer = new ResizeObserver(() => {
            // clientWidth = visible width of the scroll container (excludes scrollbar)
            // scrollWidth of inner = natural width of all content
            setIsFitting(
                innerEl.scrollWidth <=
                    scrollEl.clientWidth - WCL_GRID_PADDING_SM * 2
            );
        });

        observer.observe(scrollEl);
        observer.observe(innerEl);

        return () => observer.disconnect();
    }, []);

    const { ref: firstItemRef, inView: isStartVisible } = useInView({
        root: scrollRef?.current,
        threshold: 1
    });
    const { ref: lastItemRef, inView: isEndVisible } = useInView({
        root: scrollRef?.current,
        threshold: 1
    });

    const handleChange = useCallback((direction: number) => {
        const el = scrollRef.current;
        if (!el) {
            return;
        }

        const pageAmount = Math.min(WCL_GRID_MAX_WIDTH, el.clientWidth);

        el.scrollTo({
            top: 0,
            left: el.scrollLeft + (pageAmount - SCROLL_BUFFER) * direction,
            behavior: isReducedMotion() ? "auto" : "smooth"
        });
    }, []);

    // Entry animation trigger
    const { ref: containerRef, inView: isGalleryInView } = useInView({
        threshold: 0.25,
        triggerOnce: true
    });

    return (
        <div
            className="gallery-container"
            css={galleryStyles}
            style={
                gap
                    ? ({
                          "--gc-gap-below-md": `${gap.belowMd}`,
                          "--gc-gap-above-md": `${gap.aboveMd}`
                      } as CSSProperties)
                    : undefined
            }
            ref={containerRef}
        >
            <div
                css={[
                    scrollableStyles,
                    isMotionAvailable && scrollableAnimation,
                    isFitting && scrollableCenteredStyles
                ]}
                ref={scrollRef}
            >
                <div
                    css={[
                        innerStyles,
                        motionDelay &&
                            css({
                                transitionDuration: motionDelay
                            }),
                        showEntry && entrySetup,
                        showEntry && isGalleryInView && entryAnimation
                    ]}
                    ref={innerRef}
                >
                    <span aria-hidden="true" ref={firstItemRef} />
                    {children}
                    <span aria-hidden="true" ref={lastItemRef} />
                </div>
            </div>
            {!isFitting && (
                <ColumnLayout layout="12" horizontalAlignment="end">
                    <div
                        css={[
                            controlsStyles,
                            isStartVisible &&
                                isEndVisible &&
                                controlsHiddenStyles
                        ]}
                    >
                        <GalleryControls
                            isFirstItemVisible={isStartVisible}
                            isLastItemVisible={isEndVisible}
                            onPrevious={() => handleChange(-1)}
                            onNext={() => handleChange(1)}
                            size="large"
                        />
                    </div>
                </ColumnLayout>
            )}
        </div>
    );
};
